// vistas/PedidosExcel.jsx
import React, { useRef, useState, useMemo } from 'react';
import { 
  FileSpreadsheet, Download, Upload, Loader2, CheckCircle,
  Users, ChevronRight, Package, X, Percent, TrendingDown,
  AlertTriangle, ChevronDown
} from 'lucide-react';
import * as XLSX from 'xlsx';
import VistaResumen from './PedidosResumen';
import { useQuery } from '../QueryContext';

const formatUSD = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num || 0);

// ================================================================
// FUNCIÓN PARA LIMPIAR PORCENTAJES
// ================================================================
const limpiarPorcentaje = (valor) => {
  if (valor === null || valor === undefined) return 0;
  
  let valorStr = String(valor).trim();
  if (valorStr === '' || valorStr === '-') return 0;
  
  valorStr = valorStr.replace(',', '.');
  const tienePorcentaje = valorStr.includes('%');
  valorStr = valorStr.replace(/%/g, '').trim();
  
  let numero = parseFloat(valorStr);
  if (isNaN(numero)) return 0;
  
  if (!tienePorcentaje && numero > 0 && numero <= 1) {
    numero = numero * 100;
  }
  
  if (numero < 0) return 0;
  if (numero > 100) return 100;
  
  if (Math.abs(numero - Math.round(numero)) < 0.000001) {
    numero = Math.round(numero);
  }
  
  return numero;
};

// ================================================================
// COMPONENTE PRINCIPAL
// ================================================================
const VistaCargaExcel = ({ hook }) => {
  const { getProductosCliente } = useQuery();
  const fileInputRef = useRef(null);

  const {
    setSelectedClients,
    handleAddProduct,
    handleUpdateClientQuantity,
    successModal,
    closeSuccessModal,
    clientesOptions,
    limpiarFormulario,
    handleUpdateClientDiscount,
    handleUpdateClientPacDiscount,
    setClientOCs,    // ✅ Para autocompletar la OC
  } = hook;

  // Estados del Excel
  const [excelData, setExcelData] = useState([]);
  const [excelErrors, setExcelErrors] = useState([]);
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);
  const [advertencias, setAdvertencias] = useState([]);
  const [productosExcluidos, setProductosExcluidos] = useState([]);
  const [mostrarDetalleExcluidos, setMostrarDetalleExcluidos] = useState(false);
  const [excluidosPorCliente, setExcluidosPorCliente] = useState({});
  const [ocsPorCliente, setOcsPorCliente] = useState({}); // ✅ Estado para OCs del Excel

  // Estado del cliente seleccionado
  const [clienteActivo, setClienteActivo] = useState(null);
  const [clientesProcesados, setClientesProcesados] = useState(new Set());
  const [clientesExcluidos, setClientesExcluidos] = useState(new Set());

  // ================================================================
  // CLIENTES ÚNICOS DEL EXCEL
  // ================================================================
  const clientesExcel = useMemo(() => {
    const mapa = new Map();
    excelData.forEach(row => {
      const clienteId = row.co_cli;
      if (clientesProcesados.has(clienteId) || clientesExcluidos.has(clienteId)) return;
      
      if (!mapa.has(clienteId)) {
        const clienteInfo = clientesOptions.find(c => c.value === clienteId);
        mapa.set(clienteId, {
          id: clienteId,
          nombre: clienteInfo?.nombre || `Cliente ${clienteId}`,
          porc_desc: clienteInfo?.porc_desc || 0,
          isExento: clienteInfo?.isExento || false,
          totalItems: 0,
          totalBultos: 0,
          productosConDescuento: 0,
          productosConPAC: 0,
          oc: row.oc || '', // ✅ Agregar OC al cliente
        });
      }
      const info = mapa.get(clienteId);
      info.totalItems += 1;
      info.totalBultos += row.bultos || 0;
      
      if (row.descuento > 0) info.productosConDescuento += 1;
      if (row.pac > 0) info.productosConPAC += 1;
      
      // ✅ Si la OC viene vacía en esta fila pero ya existe, mantener la primera
      if (!info.oc && row.oc) {
        info.oc = row.oc;
      }
    });
    return [...mapa.values()];
  }, [excelData, clientesOptions, clientesProcesados, clientesExcluidos]);

  // ================================================================
  // EXCLUIR CLIENTE
  // ================================================================
  const handleExcluirCliente = (e, clienteId) => {
    e.stopPropagation();
    setClientesExcluidos(prev => {
      const nuevo = new Set(prev);
      nuevo.add(clienteId);
      return nuevo;
    });
    if (clienteActivo === clienteId) {
      setClienteActivo(null);
      setSelectedClients([]);
      limpiarFormulario();
      setProductosExcluidos([]);
    }
  };

  // ================================================================
  // RESTAURAR CLIENTE EXCLUIDO
  // ================================================================
  const handleRestaurarCliente = (clienteId) => {
    setClientesExcluidos(prev => {
      const nuevo = new Set(prev);
      nuevo.delete(clienteId);
      return nuevo;
    });
  };

  // ================================================================
  // PROCESAR ARCHIVO EXCEL
  // ================================================================
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setIsProcessingExcel(true);
    setExcelErrors([]);
    setAdvertencias([]);
    setClientesProcesados(new Set());
    setClientesExcluidos(new Set());
    setProductosExcluidos([]);
    setExcluidosPorCliente({});
    setOcsPorCliente({}); // ✅ Limpiar OCs
    limpiarFormulario();
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      
      const rawData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1, 
        defval: '',
        raw: false
      });
      
      if (rawData.length < 2) {
        setExcelErrors(['El archivo está vacío o no tiene datos']);
        setIsProcessingExcel(false);
        return;
      }
      
      const headers = rawData[0].map(h => {
        if (h === null || h === undefined) return '';
        return String(h).trim().toUpperCase().replace(/\s+/g, ' ');
      });
      
      console.log('Headers encontrados:', headers);
      
      const idxCliente = headers.findIndex(h => 
        h === 'CO_CLI' || h === 'CO CLI' || h === 'CLIENTE' || h === 'COD_CLI' || h === 'CODIGO_CLIENTE'
      );
      const idxSKU = headers.findIndex(h => 
        h === 'CO_ART' || h === 'CO ART' || h === 'ARTICULO' || h === 'SKU' || h === 'COD_ART' || h === 'CODIGO_ARTICULO'
      );
      const idxBultos = headers.findIndex(h => 
        h === 'BULTOS' || h === 'BULTO' || h === 'CANTIDAD' || h === 'CANT' || h === 'QTY'
      );
      const idxDescuento = headers.findIndex(h => 
        h === 'DESCUENTO' || h === 'DESC' || h === 'DESCUENTO%' || h === 'DESC%'
      );
      const idxPAC = headers.findIndex(h => 
        h === 'PAC' || h === 'PAC%' || h === 'PRECIO_A_CONSIGNAR'
      );
      // ✅ NUEVO: Buscar columna de OC
      const idxOC = headers.findIndex(h => 
        h === 'OC' || h === 'ORDEN_COMPRA' || h === 'ORDEN DE COMPRA' || h === 'ORDEN_COMPRA' || h === 'NRO_OC' || h === 'NUM_OC'
      );
      
      if (idxCliente === -1 || idxSKU === -1 || idxBultos === -1) {
        setExcelErrors([
          'Faltan columnas requeridas. El archivo debe tener: CO_CLI, CO_ART, BULTOS',
          `Columnas encontradas: ${headers.join(', ')}`
        ]);
        setIsProcessingExcel(false);
        return;
      }
      
      const rows = [];
      const errors = [];
      const warnings = [];
      let descuentosLimpios = 0;
      let pacLimpios = 0;
      
      // ✅ Mapa temporal para OCs
      const ocsTemp = {};
      
      for (let i = 1; i < rawData.length; i++) {
        const row = rawData[i];
        if (!row || row.length === 0) continue;
        
        const values = row.map(v => {
          if (v === null || v === undefined) return '';
          return String(v).trim();
        });
        
        if (values.every(v => v === '')) continue;
        
        const co_cli = String(values[idxCliente] || '').padStart(4, '0');
        const co_art = values[idxSKU] || '';
        const bultos = parseInt(values[idxBultos]) || 0;
        
        // ✅ Leer OC si existe la columna
        let oc = '';
        if (idxOC !== -1 && values[idxOC] !== '') {
          oc = values[idxOC];
        }
        
        // Guardar OC por cliente (la primera que aparezca)
        if (oc && !ocsTemp[co_cli]) {
          ocsTemp[co_cli] = oc;
        }
        
        let descuento = 0;
        let pac = 0;
        
        if (idxDescuento !== -1 && values[idxDescuento] !== '') {
          const descuentoOriginal = values[idxDescuento];
          descuento = limpiarPorcentaje(descuentoOriginal);
          if (descuentoOriginal !== String(descuento)) {
            descuentosLimpios++;
            warnings.push(`Fila ${i + 1}: Descuento "${descuentoOriginal}" → ${descuento}%`);
          }
        }
        
        if (idxPAC !== -1 && values[idxPAC] !== '') {
          const pacOriginal = values[idxPAC];
          pac = limpiarPorcentaje(pacOriginal);
          if (pacOriginal !== String(pac)) {
            pacLimpios++;
            warnings.push(`Fila ${i + 1}: PAC "${pacOriginal}" → ${pac}%`);
          }
        }
        
        if (!co_cli || co_cli === '0000') { errors.push(`Fila ${i + 1}: CO_CLI vacío`); continue; }
        if (!co_art) { errors.push(`Fila ${i + 1}: CO_ART vacío`); continue; }
        if (bultos <= 0) { errors.push(`Fila ${i + 1}: BULTOS debe ser > 0`); continue; }
        
        rows.push({ 
          co_cli, 
          co_art, 
          bultos, 
          descuento: descuento || 0,
          pac: pac || 0,
          oc: oc, // ✅ Agregar OC a la fila
          linea: i + 1 
        });
      }
      
      // ✅ Guardar OCs por cliente
      setOcsPorCliente(ocsTemp);
      
      if (descuentosLimpios > 0 || pacLimpios > 0) {
        warnings.unshift(`ℹ️ Se convirtieron automáticamente ${descuentosLimpios} descuentos y ${pacLimpios} PAC`);
      }
      
      setExcelErrors(errors);
      setAdvertencias(warnings.slice(0, 5));
      setExcelData(rows);
      
      if (errors.length === 0) {
        console.log(`✅ Archivo procesado: ${rows.length} filas válidas`);
        console.log('📋 OCs encontradas:', ocsTemp);
      }
      
    } catch (error) {
      setExcelErrors([`Error al leer el archivo: ${error.message}`]);
      console.error('Error completo:', error);
    } finally {
      setIsProcessingExcel(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ================================================================
  // DESCARGAR PLANTILLA
  // ================================================================
  const handleDownloadTemplate = () => {
    const data = [
      ['CO_CLI', 'CO_ART', 'BULTOS', 'DESCUENTO', 'PAC', 'OC'],
      ['0001', '0301020120048', '10', '5', '', 'P443 Trinidad'],
      ['0001', '0601020030061', '20', '', '10', 'P443 Trinidad'],
      ['0002', '0301020120048', '5', '15%', '', 'OC-2024-001'],
      ['0002', '0401030050077', '8', '', '20%', 'OC-2024-001'],
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [{ wch: 12 }, { wch: 18 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Pedido Masivo');
    XLSX.writeFile(wb, 'plantilla_pedido_masivo.xlsx');
  };

  // ================================================================
  // SELECCIONAR CLIENTE Y PROCESAR SU PEDIDO
  // ================================================================
  const handleSelectCliente = async (clienteId) => {
    const clienteIdNormalizado = String(clienteId).padStart(4, '0');
    
    if (clienteActivo === clienteIdNormalizado) {
      setClienteActivo(null);
      setSelectedClients([]);
      limpiarFormulario();
      setProductosExcluidos([]);
      setMostrarDetalleExcluidos(false);
      return;
    }
    
    limpiarFormulario();
    setProductosExcluidos([]);
    setMostrarDetalleExcluidos(false);
    
    setClienteActivo(clienteIdNormalizado);
    setSelectedClients([clienteIdNormalizado]);
    
   // ✅ AUTOCOMPLETAR LA OC DEL EXCEL (cortando a 25 caracteres)
    const ocDelExcel = String(ocsPorCliente[clienteIdNormalizado] || '')
      .trim()
      .slice(0, 25);  // ✅ Cortar a 25 caracteres máximo

    if (ocDelExcel && setClientOCs) {
      setClientOCs(prev => ({
        ...prev,
        [clienteIdNormalizado]: 'OC - ' + ocDelExcel
      }));
      console.log(`✅ OC autocompletada para ${clienteIdNormalizado}: ${ocDelExcel}`);
    }
    
    try {
      const data = await getProductosCliente(clienteIdNormalizado);
      if (data?.success) {
        const filasCliente = excelData.filter(row => row.co_cli === clienteIdNormalizado);
        
        const noProcesados = [];
        const procesados = [];
        const skusConvertidos = [];
        
        filasCliente.forEach(fila => {
          const codigoOriginal = String(fila.co_art).trim();
          const codigoLimpio = codigoOriginal.replace(/^0+/, '');
          const codigoConCeros = codigoLimpio.padStart(13, '0');
          
          // Buscar por SKU
          let producto = data.productos.find(p => {
            const pLimpio = String(p.sku).replace(/^0+/, '');
            return pLimpio === codigoLimpio || p.sku === codigoConCeros;
          });
          
          // Buscar por REF (código de barras)
          if (!producto) {
            producto = data.productos.find(p => {
              if (!p.ref) return false;
              const refLimpio = String(p.ref).replace(/^0+/, '').trim();
              const codigoSinCeros = codigoOriginal.replace(/^0+/, '').trim();
              return refLimpio === codigoSinCeros || 
                     refLimpio === codigoOriginal || 
                     p.ref === codigoOriginal ||
                     p.ref === codigoConCeros;
            });
            
            if (producto) {
              skusConvertidos.push({
                codigoOriginal,
                skuEncontrado: producto.sku,
                ref: producto.ref,
                linea: fila.linea
              });
            }
          }
          
          // No encontrado
          if (!producto) {
            noProcesados.push({
              codigo_barra: codigoOriginal,
              sku: null,
              ref: null,
              linea: fila.linea,
              motivo: 'NO ENCONTRADO'
            });
            return;
          }
          
          // Verificar precio
          const precioUSD = producto.precio_usd || 0;
          const precioBS = producto.precio_bs || 0;
          
          if (precioUSD <= 0 && precioBS <= 0) {
            noProcesados.push({
              codigo_barra: codigoOriginal,
              sku: producto.sku,
              ref: producto.ref || null,
              linea: fila.linea,
              motivo: 'INACTIVO'
            });
            return;
          }
          
          // Procesado correctamente
          procesados.push({
            codigo_barra: codigoOriginal,
            sku: producto.sku,
            linea: fila.linea
          });
          
          const productFormatted = {
            sku: producto.sku,
            marca: producto.marca,
            nombre: producto.nombre,
            uni_venta: producto.uni_venta || 'UND',
            tipo_imp: producto.tipo_imp || 1,
            equivalencia: producto.equivalencia || 1.0,
            stock_bl: producto.stock_bl,
            ref: producto.ref || null,
            clientPrecios: {
              [clienteIdNormalizado]: {
                precio_bs: precioBS,
                precio_bs2: parseFloat(producto.precio_bs2 || 0) || 0,
                precio_usd: precioUSD,
                codigo_vendedor: producto.codigo_vendedor || '01',
                cos_pro_un: parseFloat(producto.cos_pro_un || 0) || 0,
                ult_cos_un: parseFloat(producto.ult_cos_un || 0) || 0,
                cos_pro_om: parseFloat(producto.cos_pro_om || 0) || 0,
                ult_cos_om: parseFloat(producto.ult_cos_om || 0) || 0
              }
            }
          };
          
          handleAddProduct(productFormatted);
          handleUpdateClientQuantity(producto.sku, clienteIdNormalizado, fila.bultos);
          
          if (fila.descuento > 0 && handleUpdateClientDiscount) {
            handleUpdateClientDiscount(producto.sku, clienteIdNormalizado, fila.descuento);
          }
          
          if (fila.pac > 0 && handleUpdateClientPacDiscount) {
            handleUpdateClientPacDiscount(producto.sku, clienteIdNormalizado, fila.pac);
          }
        });
        
        // Guardar excluidos
        setProductosExcluidos(noProcesados);
        setExcluidosPorCliente(prev => ({
          ...prev,
          [clienteIdNormalizado]: noProcesados
        }));
        
        if (noProcesados.length > 0) {
          console.warn('❌ Productos excluidos:', noProcesados);
        }
        
        if (skusConvertidos.length > 0) {
          console.log('🔄 Códigos convertidos:', skusConvertidos);
        }
        
        console.log(`✅ Procesados: ${procesados.length} | ❌ Excluidos: ${noProcesados.length}`);
      }
    } catch (err) {
      console.error('Error procesando cliente:', err);
    }
  };

  // ================================================================
  // MARCAR CLIENTE COMO PROCESADO
  // ================================================================
  const handleCloseSuccessModal = () => {
    if (clienteActivo) {
      setClientesProcesados(prev => {
        const nuevo = new Set(prev);
        nuevo.add(clienteActivo);
        return nuevo;
      });
    }
    closeSuccessModal();
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '0', height: '100%', overflow: 'hidden' }}>
      {/* COLUMNA 1 - CLIENTES DEL EXCEL */}
      <div className="mass-col mass-col-clients">
        <div className="col-header">
          <Users size={18} />
          <h3>Clientes Excel</h3>
          {clientesExcel.length > 0 && <span className="badge">{clientesExcel.length}</span>}
        </div>

        <div style={{ padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="new-order-btn"
            style={{ padding: '8px 12px', fontSize: '0.7rem', minWidth: 'auto', background: 'var(--text-theme, #00ba52)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
          >
            <Upload size={14} /> Subir Excel
          </button>
          <button
            onClick={handleDownloadTemplate}
            className="new-order-btn"
            style={{ padding: '6px 12px', fontSize: '0.65rem', minWidth: 'auto', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
          >
            <Download size={12} /> Plantilla
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload} style={{ display: 'none' }} />
        </div>

        {isProcessingExcel && (
          <div className="loading-state"><Loader2 size={20} className="spin-anim" /><p>Procesando...</p></div>
        )}

        {excelErrors.length > 0 && (
          <div style={{ padding: '0.5rem', background: 'rgba(239,68,68,0.1)', borderBottom: '1px solid rgba(239,68,68,0.2)' }}>
            {excelErrors.slice(0, 5).map((err, i) => (
              <div key={i} style={{ color: '#f87171', fontSize: '0.55rem' }}>⚠️ {err}</div>
            ))}
          </div>
        )}

        {advertencias.length > 0 && excelErrors.length === 0 && (
          <div style={{ padding: '0.5rem', background: 'rgba(59,130,246,0.1)', borderBottom: '1px solid rgba(59,130,246,0.2)' }}>
            {advertencias.map((adv, i) => (
              <div key={i} style={{ color: '#60a5fa', fontSize: '0.5rem' }}>ℹ️ {adv}</div>
            ))}
          </div>
        )}

        <div className="client-list">
          {clientesExcel.length === 0 && !isProcessingExcel && (
            <div className="empty-state">
              <FileSpreadsheet size={30} />
              <p style={{ fontSize: '0.7rem' }}>
                {excelData.length > 0 ? 'Todos los clientes fueron procesados o excluidos' : 'Sube un Excel para ver clientes'}
              </p>
            </div>
          )}
          {clientesExcel.map(cliente => {
            const excluidosCliente = excluidosPorCliente[cliente.id] || [];
            const totalExcluidos = excluidosCliente.length;
            
            return (
              <div
                key={cliente.id}
                className={`client-item ${clienteActivo === cliente.id ? 'selected' : ''}`}
                onClick={() => handleSelectCliente(cliente.id)}
                style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px', padding: '10px 12px', position: 'relative' }}
              >
                <button
                  onClick={(e) => handleExcluirCliente(e, cliente.id)}
                  style={{
                    position: 'absolute', top: '6px', right: '6px',
                    background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
                    color: '#f87171', cursor: 'pointer', padding: '2px',
                    borderRadius: '50%', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', width: '20px', height: '20px'
                  }}
                  title="Excluir cliente"
                >
                  <X size={10} />
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', paddingRight: '24px' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#00ba52', fontFamily: 'monospace' }}>
                    {cliente.id}
                  </span>
                  {cliente.isExento && (
                    <span style={{ fontSize: '0.45rem', fontWeight: 700, background: 'rgba(251,191,36,0.15)', color: '#fbbf24', padding: '1px 5px', borderRadius: '6px' }}>
                      EXENTO
                    </span>
                  )}
                  {cliente.porc_desc > 0 && (
                    <span style={{ fontSize: '0.45rem', fontWeight: 600, background: 'rgba(239,68,68,0.15)', color: '#f87171', padding: '1px 5px', borderRadius: '6px' }}>
                      {cliente.porc_desc}% DESC
                    </span>
                  )}
                  {clienteActivo === cliente.id && (
                    <ChevronRight size={14} style={{ color: '#00ba52', marginLeft: 'auto' }} />
                  )}
                </div>
                <span style={{ fontSize: '0.6rem', color: 'white' }}>{cliente.nombre}</span>
                
                {/* ✅ MOSTRAR OC DEL CLIENTE */}
                {cliente.oc && (
                  <span style={{ 
                    fontSize: '0.5rem', 
                    color: '#4ade80', 
                    background: 'rgba(74,222,128,0.1)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontFamily: 'monospace'
                  }}>
                    OC: {cliente.oc}
                  </span>
                )}
                
                <div style={{ display: 'flex', gap: '10px', fontSize: '0.55rem', color: 'white', flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <Package size={10} /> {cliente.totalItems} items
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <FileSpreadsheet size={10} /> {cliente.totalBultos} bultos
                  </span>
                  
                  {totalExcluidos > 0 && (
                    <span style={{ 
                      display: 'flex', alignItems: 'center', gap: '3px',
                      color: '#f87171', fontWeight: 700 
                    }}>
                      <AlertTriangle size={10} /> {totalExcluidos} excl
                    </span>
                  )}
                  
                  {cliente.productosConDescuento > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#60a5fa', fontWeight: 600 }}>
                      <TrendingDown size={10} /> {cliente.productosConDescuento} desc
                    </span>
                  )}
                  {cliente.productosConPAC > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#a78bfa', fontWeight: 600 }}>
                      <Percent size={10} /> {cliente.productosConPAC} PAC
                    </span>
                  )}
                </div>
                
                {totalExcluidos > 0 && (
                  <div style={{ 
                    fontSize: '0.45rem', 
                    color: '#fca5a5',
                    fontFamily: 'monospace',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '3px',
                    width: '100%'
                  }}>
                    {excluidosCliente.slice(0, 3).map((p, idx) => (
                      <span key={idx} style={{
                        background: 'rgba(239,68,68,0.1)',
                        padding: '1px 4px',
                        borderRadius: '3px',
                        whiteSpace: 'nowrap'
                      }}>
                        {p.codigo_barra}
                      </span>
                    ))}
                    {totalExcluidos > 3 && (
                      <span style={{ color: '#f87171' }}>+{totalExcluidos - 3}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {clientesExcluidos.size > 0 && (
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '0.5rem' }}>
            <h4 style={{ fontSize: '0.65rem', color: '#f87171', marginBottom: '0.3rem' }}>
              Excluidos ({clientesExcluidos.size}):
            </h4>
            {[...clientesExcluidos].map(clienteId => (
              <div key={clienteId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 6px', fontSize: '0.6rem', color: '#888' }}>
                <span>{clienteId}</span>
                <button
                  onClick={() => handleRestaurarCliente(clienteId)}
                  style={{ background: 'none', border: 'none', color: '#00ba52', cursor: 'pointer', fontSize: '0.55rem' }}
                >
                  Restaurar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* COLUMNA 2 - RESUMEN CON PANEL DE EXCLUIDOS */}
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {productosExcluidos.length > 0 && (
          <div style={{
            padding: '4px 10px',
            background: 'rgba(239,68,68,0.08)',
            borderBottom: '2px solid rgba(239,68,68,0.3)',
            flexShrink: 0
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              cursor: 'pointer'
            }} onClick={() => setMostrarDetalleExcluidos(!mostrarDetalleExcluidos)}>
              <AlertTriangle size={11} style={{ color: '#f87171', flexShrink: 0 }} />
              <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#f87171' }}>
                {productosExcluidos.length} excluidos
              </span>
              <span style={{ 
                fontSize: '0.55rem', 
                fontFamily: 'monospace', 
                color: '#fca5a5',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1
              }}>
                {productosExcluidos.map(p => p.codigo_barra).join(', ')}
              </span>
              <ChevronDown 
                size={10} 
                style={{ 
                  color: '#f87171', 
                  transform: mostrarDetalleExcluidos ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s'
                }} 
              />
            </div>
            
            {mostrarDetalleExcluidos && (
              <div style={{ 
                marginTop: '4px', 
                maxHeight: '80px', 
                overflowY: 'auto',
                paddingLeft: '15px'
              }}>
                {productosExcluidos.map((p, idx) => (
                  <div key={idx} style={{
                    fontSize: '0.55rem',
                    fontFamily: 'monospace',
                    color: '#fca5a5',
                    padding: '1px 0'
                  }}>
                    L{p.linea}: {p.codigo_barra}
                    {p.sku ? ` → ${p.sku}` : ''}
                    <span style={{ color: '#f87171', fontSize: '0.45rem' }}>
                      {' '}({p.motivo})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        <VistaResumen hook={hook} />
      </div>

      {/* MODAL DE ÉXITO - SIN CAMBIOS */}
      {successModal.show && (
        <>
          <div className="order-panel-overlay" style={{ zIndex: 1001 }} onClick={handleCloseSuccessModal} />
          <div style={{ 
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
            background: 'rgba(15, 20, 35, 0.98)', backdropFilter: 'blur(20px)', 
            borderRadius: '20px', padding: '2rem', textAlign: 'center', zIndex: 1002, 
            boxShadow: '0 10px 30px rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.08)', 
            width: '90%', maxWidth: '550px', maxHeight: '80vh', overflow: 'auto', 
            fontFamily: 'Montserrat, sans-serif' 
          }}>
            <CheckCircle size={48} style={{ color: '#00ba52', marginBottom: '1rem' }} />
            <p style={{ color: '#fff', fontSize: '1rem', fontWeight: 600, margin: '0.5rem 0' }}>
              {successModal.message}
            </p>

            {successModal.skus_no_procesados && successModal.skus_no_procesados.length > 0 && (
              <div style={{ 
                marginTop: '1rem', textAlign: 'left', 
                background: 'rgba(239,68,68,0.1)', 
                border: '1px solid rgba(239,68,68,0.3)', 
                borderRadius: '8px', padding: '0.8rem' 
              }}>
                <h4 style={{ color: '#f87171', fontSize: '0.8rem', marginBottom: '0.5rem', fontWeight: 600 }}>
                  ⚠️ SKUs no procesados ({successModal.skus_no_procesados.length}):
                </h4>
                <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                  {successModal.skus_no_procesados.map((sku) => (
                    <div key={sku} style={{ 
                      color: '#f87171', fontSize: '0.7rem', fontFamily: 'monospace', 
                      padding: '2px 0', borderBottom: '1px solid rgba(239,68,68,0.1)' 
                    }}>
                      • {sku}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {successModal.pedidos && successModal.pedidos.length > 0 && (
              <div style={{ marginTop: '1rem', textAlign: 'left' }}>
                {successModal.pedidos.map((pedido, idx) => (
                  <div key={idx} style={{ 
                    background: 'rgba(255,255,255,0.05)', borderRadius: '8px', 
                    padding: '0.5rem', marginBottom: '0.5rem', 
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
                  }}>
                    <div>
                      <div style={{ color: '#00ba52', fontWeight: 600 }}>
                        Pedido #{pedido.factNum}
                        {pedido.lote && <span style={{ fontSize: '0.7rem', color: '#888' }}> (Lote {pedido.lote})</span>}
                      </div>
                      <div style={{ color: '#aaa', fontSize: '0.8rem' }}>
                        Cliente: {pedido.clienteId} | {pedido.lineas} producto(s) | {pedido.moneda}
                      </div>
                    </div>
                    <div style={{ color: '#fff', fontWeight: 700 }}>{formatUSD(pedido.total)}</div>
                  </div>
                ))}
              </div>
            )}

            <button onClick={handleCloseSuccessModal} style={{ 
              marginTop: '1.5rem', background: '#00ba52', color: '#000', 
              border: 'none', padding: '0.7rem 2rem', borderRadius: '10px', 
              fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', 
              fontFamily: 'Montserrat, sans-serif', boxShadow: '0 4px 10px rgba(0,186,82,0.3)' 
            }}>
              Aceptar
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default VistaCargaExcel;